import { useFeatureFlagEnabled, useFeatureFlagPayload } from "posthog-js/react";
import { useMerchant, useUser, canUseAi } from "@auth";
import { aiModule } from "@auth/modules";

// NOT USED, leaving  as example till we do another one
export const FEATURE_FLAGS = {
  PDF_STATEMENTS: "pdfStatements",
  CONSOLE_LOGGING: "consoleLogging",
  SHOW_FILE_IMPORT: "showFileImport",
  CHATBOT: "chatbot",
  REQUEST_TEMPLATES: "requestTemplates",
  EARLY_SETTLEMENT_DISCOUNT: "earlySettlementDiscount",
  PAYMENT_REQUEST_LABELS: "paymentRequestLabels",
  QUERIES: "queries",
  HISTORICAL_ANALYSIS_MCP: "historical-analysis-mcp",
  PORTAL_NOTIFICATIONS: "portalNotifications",
  AI_RESPONSE_TIMER: "aiResponseTimer",
  SMART_NOTE_V2: "smartNoteV2",
  HOME_V2: "homeV2",
  SUGGESTION_CATALOGUE: "suggestion-catalogue",
};

const isDevHost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location?.hostname || "";
  return host.includes("dev") || host.includes("localhost") || host === "127.0.0.1";
};

const useFeatureFlagDevAlwaysEnabled = (flagKey) => {
  const enabled = useFeatureFlagEnabled(flagKey);

  if (isDevHost()) {
    return true;
  }

  return enabled;
};

export const usePdfStatements = () => {
  return useFeatureFlagEnabled(FEATURE_FLAGS.PDF_STATEMENTS);
};

export const useConsoleLogging = () => {
  return useFeatureFlagEnabled(FEATURE_FLAGS.CONSOLE_LOGGING);
};

export const useShowFileImport = () => {
  return useFeatureFlagEnabled(FEATURE_FLAGS.SHOW_FILE_IMPORT);
};

export const useChatbot = () => {
  return useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.CHATBOT);
};

export const useRequestTemplates = () => {
  return useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.REQUEST_TEMPLATES);
};
export const useEarlySettlementDiscount = () => {
  return useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.EARLY_SETTLEMENT_DISCOUNT);
}

export const usePaymentRequestLabels = () => {
  return useFeatureFlagEnabled(FEATURE_FLAGS.PAYMENT_REQUEST_LABELS);
};

export const useQueries = () => {
  return useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.QUERIES);
};

export const useHistoricalAnalysisMcp = () => {
  return useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.HISTORICAL_ANALYSIS_MCP);
};

export const usePortalNotifications = () => useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.PORTAL_NOTIFICATIONS);

export const useAiResponseTimer = () => useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.AI_RESPONSE_TIMER);

// New unified account view for the spacebar / Smart Note flow (account header + read + chat on the
// left, tabbed Balance/Activity/Documents/Account panel on the right). Dev-on so it can be tested
// locally; gated by PostHog everywhere else.
export const useSmartNoteV2 = () => useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.SMART_NOTE_V2);

// New Home/Tasks page and working sets (incl. ?set= / ?newWorkingSet on Trade accounts), rolled out
// separately from the Smart Note V2 modal. Dev-on; gated by PostHog everywhere else.
export const useHomeV2 = () => useFeatureFlagDevAlwaysEnabled(FEATURE_FLAGS.HOME_V2);

// Composer autocomplete from the curated suggestion catalogue (matched in the browser) instead of the
// per-keystroke model — the flag splits the two arms for comparison.
export const useCatalogueAutocomplete = () => useFeatureFlagEnabled(FEATURE_FLAGS.SUGGESTION_CATALOGUE);

export const useAiFeatures = () => {
  const { hasModule, hasPermission } = useMerchant();
  const { user } = useUser();

  if (isDevHost()) {
    return true;
  }

  // Ontik support users, and users with the per-user override, always have access —
  // regardless of the merchant's AI module or role permissions.
  if (user?.isSupportUser || user?.aiFeaturesEnabled) {
    return true;
  }

  return hasModule(aiModule) && hasPermission(canUseAi);
};